import type { LongWindowNote } from '@kkp-fraud/shared';
import { t } from '../i18n';
import { longWindowPlace, sinceNoteOf, sinceNoteText, sinceNotesOf } from '../longWindows';

/**
 * Wave 7 long windows — where each long-window measure a dry run read starts counting: "counting
 * since 28/09/2026", or "not counting yet — counts from the day this rule is released" for a rule
 * that has never been released (history is never back-filled). A `distinct` count is an estimate
 * and says so. Nothing is shown when the dry run read no long window.
 */
export function LongWindowNotes({ notes, testid = 'long-window-notes' }: { notes: readonly LongWindowNote[] | undefined; testid?: string }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className="stack" data-testid={testid} style={{ gap: 4 }}>
      <span className="note" style={{ margin: 0 }}>
        {t('dryrun.longWindows.title')}
      </span>
      <ul className="long-window-notes" aria-label={t('dryrun.longWindows.title')}>
        {notes.map((n) => {
          const note = sinceNoteOf(n.since);
          return (
            <li
              key={`${n.ruleSetId}/${n.ruleId}/${n.row ?? 0}/${n.measure ?? 0}`}
              className="small"
              data-testid="long-window-note"
              data-rule={n.ruleId}
              data-since={n.since ?? ''}
            >
              <span className="mono">{longWindowPlace(n)}</span>
              {' — '}
              <span className={note.kind === 'notYet' ? 'muted' : undefined}>{sinceNoteText(note)}</span>
              {n.approximate && (
                <>
                  {' '}
                  <span className="chip info">{t('longWindow.approximate')}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A hit's evidence string with, under it, the "since" note of every long-window measure it
 * names (`since YYYY-MM-DD` → "counting since 28/09/2026"; `no history yet` → "not counting
 * yet …"). A `≈` the engine prints before an estimate stays in the string as it is.
 */
export function HitDetail({ detail }: { detail: string | null | undefined }) {
  const notes = sinceNotesOf(detail);
  if (!detail) return <>—</>;
  return (
    <>
      {detail}
      {notes.map((note) => (
        <div key={note.kind === 'since' ? note.day : 'not-yet'} className="small muted" data-testid="hit-since-note">
          {sinceNoteText(note)}
        </div>
      ))}
    </>
  );
}
